export interface BrowserLocationSnapshot {
	hash: string;
	pathname: string;
	search: string;
}

export interface PageResetState {
	browserPath: string;
	locationKey: string;
	resetKey: number;
}

const SAME_PAGE_HISTORY_CHANGE_EVENT = 'glai:same-page-history-change';

/**
 * A deliberate navigation to the URL already in the address bar means "show this
 * destination again". React Router publishes a new location key for that action,
 * but the path itself cannot drive route effects because it did not change. Advance
 * a page-only reset key in that one case; ordinary navigation and page-local history
 * updates preserve the mounted page.
 */
export function resolvePageResetState(previous: PageResetState, browserPath: string, locationKey: string): PageResetState {
	if (previous.browserPath === browserPath && previous.locationKey === locationKey) return previous;

	return {
		browserPath,
		locationKey,
		resetKey: previous.resetKey + (previous.browserPath === browserPath && previous.locationKey !== locationKey ? 1 : 0)
	};
}

export function readBrowserLocation(fallback: BrowserLocationSnapshot): BrowserLocationSnapshot {
	if (typeof window === 'undefined') return fallback;

	return {
		hash: window.location.hash,
		pathname: window.location.pathname,
		search: window.location.search
	};
}

export function notifySamePageHistoryChange(): void {
	if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;

	window.dispatchEvent(new Event(SAME_PAGE_HISTORY_CHANGE_EVENT));
}

export function subscribeToSamePageHistoryChanges(listener: () => void): () => void {
	if (typeof window === 'undefined') return () => undefined;

	window.addEventListener(SAME_PAGE_HISTORY_CHANGE_EVENT, listener);
	return () => window.removeEventListener(SAME_PAGE_HISTORY_CHANGE_EVENT, listener);
}
